import { RubricBankItem } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item';
import { RubricBankItemForWrite } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { WritePatternHook } from 'apps/sigma2-ui/src/lib/hooks/useWritePattern';

export type UseRubricItemsDataPayload = {
  items: RubricBankItem[];
  organizationId: string;
  databaseId: string;
  readPattern: ReadPatternHook<RubricBankItem[]>;
  createItemPattern: WritePatternHook<EmptyObject, RubricBankItemForWrite>;
  singleSelectConfig: {
    currentItemId: string | null;
    setCurrentItemId: (itemId: string | null) => void;
  } | null;
};

export const useRubricItemsData = ({
  organizationId,
  databaseId,
  singleSelectConfig,
}: {
  organizationId: string;
  databaseId: string;
  singleSelectConfig: {
    initialSelectedItemId: string | null;
  } | null;
}): UseRubricItemsDataPayload => {
  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items`;
  }, [organizationId, databaseId]);

  const readItemsPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: RubricBankItem.array(),
  });

  const createItemPattern = useSimpleWritePattern({
    getUrlFn,
    method: 'POST',
    canWrite: true,
    writeSchema: RubricBankItemForWrite,
    responseSchema: EmptyObject,
  });

  const items = useMemo(() => {
    return readItemsPattern.status === 'done' ? readItemsPattern.entity : [];
  }, [readItemsPattern]);

  const [currentItemId, setCurrentItemId] = useState<string | null>(
    singleSelectConfig?.initialSelectedItemId ?? null,
  );

  return useMemo(() => {
    return {
      items,
      organizationId,
      databaseId,
      readPattern: readItemsPattern,
      createItemPattern,
      singleSelectConfig: singleSelectConfig
        ? { currentItemId, setCurrentItemId }
        : null,
    };
  }, [
    items,
    organizationId,
    databaseId,
    currentItemId,
    singleSelectConfig,
    readItemsPattern,
    createItemPattern,
  ]);
};
